import { computed, DestroyRef, inject, Injectable, signal } from "@angular/core";
import { ActivatedRoute, UrlTree } from "@angular/router";

/*
Example usage of RoutedTitle:

@Component({
  selector: 'novus-titled-component',
  providers: [RoutedTitle],
  template: `...`
})
export class TitledComponent {
  private readonly title = inject(RoutedTitle);

  constructor(): {
    this.title.set("Very important title");
  }
}
*/

interface TitleEntry {
  id: symbol;
  title: string;
  routeTo: string | any[] | UrlTree | boolean;
  depth: number;
  order: number;
}

@Injectable({
  providedIn: 'root'
})
export class PageTitleService {
  private readonly entries = signal<TitleEntry[]>([]);
  private order = 0;

  readonly current = computed(() => {
    const entries = this.entries();

    if (entries.length === 0)
      return null;

    return entries.reduce((best, current) => {
      if (current.depth > best.depth)
        return current;

      if (
        current.depth === best.depth &&
        current.order > best.order
      )
        return current;

      return best;
    })
  });

  readonly title = computed(() => this.current()?.title);
  readonly routeTo = computed(() => this.current()?.routeTo ?? true);

  set(
    id: symbol,
    title: string,
    depth: number,
    routeTo: string | any[] | UrlTree | boolean = true): void {
    this.entries.update(entries => {
      const existing = entries.find(x => x.id === id);
      const order = this.order++;

      if (existing)
        return entries.map(x => x.id === id ? { ...x, title, routeTo, order } : x);

      return [...entries, { id, title, routeTo, depth, order }];
    });
  }

  remove(id: symbol): void {
    this.entries.update(entries =>
      entries.filter(x => x.id !== id)
    );
  }
}

@Injectable()
export class RoutedTitle {
  private readonly route = inject(ActivatedRoute);
  private readonly titleService = inject(PageTitleService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly id = Symbol();

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.titleService.remove(this.id);
    });
  }

  /**
   * 
   * @param title Desired title of the page. Styling is determined by the object displaying the title
   * @param routeTo Optional value, allows enabling, disabling, and changing routing when a user clicks on the title. Defaults managed by the object displaying the title
   */
  set(
    title: string,
    routeTo: string | any[] | UrlTree | boolean = true): void {
    this.titleService.set(
      this.id,
      title,
      this.route.pathFromRoot.length,
      routeTo
    );
  }
}